import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { EditMenuComponent } from '../../modals/edit-menu/edit-menu.component';
import { IMenu, IMenuGroup, IMenuGroupTableData, MenuFile } from '../../../interfaces/menu';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { state, style, trigger } from '@angular/animations';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { isEqual} from 'lodash';
import { MenuStoreService } from '../../../services/menu/menu-store.service';
import { AddMenuComponent } from '../../modals/add-menu/add-menu.component';

@UntilDestroy()
@Component({
  selector: 'app-menu-table',
  templateUrl: './menu-table.component.html',
  styleUrls: ['./menu-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
    ]),
  ],
})
export class MenuTableComponent implements OnInit, AfterViewInit {
  private originalMenus: IMenuGroup[] = [];
  public isLoading = false;
  public searchTerm$ = new BehaviorSubject<string>('');
  public menusData: IMenuGroupTableData[] = [];
  public edit = false;
  public displayedColumns = ['name', 'language', 'file', 'icon', 'hidden', 'edit'];
  public expandedElements: number[] = [];
  public dataSource = new MatTableDataSource<IMenuGroupTableData>();
  public pageSizeOptions = [5, 25, 50, 100];
  public pageSize = 25;

  @Input() isEdit = false;
  @Input() restaurantId!: number;
  @Input() isMainMenu = true;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  constructor(
    private readonly activateRoute: ActivatedRoute,
    private readonly menuStore: MenuStoreService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.menuStore.isLoading$.pipe(
      untilDestroyed(this)
    ).subscribe((load) => {
      this.isLoading = load;
    });

    this.menuStore.getMenus$.pipe(
      untilDestroyed(this)
    ).subscribe((menus) => {
      this.originalMenus = menus;
      this.menusData = this.buildTableData(menus);

      this.updateDataSource();
    });

    const itemPerPage = sessionStorage.getItem('menu.pageSize');
    this.pageSize = itemPerPage ? parseInt(itemPerPage, 10) : 25;

    this.dataSource.filterPredicate = (data: IMenuGroupTableData, searchParam: string) => {
      return this.createFilter(data, searchParam);
    };

    this.activateRoute.queryParams.pipe(
      map(params => params.s ?? ''),
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe((searchParam: string) => {
      this.applyFilter(searchParam);
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  private updateDataSource(): void {
    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource<IMenuGroupTableData>();
      this.dataSource.filterPredicate = (data, searchParam) =>
        this.createFilter(data, searchParam);
      this.dataSource.data = [...this.menusData];
    } else {
      const nextData = this.menusData.map((newItem) => {
        const existing = this.dataSource.data.find(d => d.id === newItem.id);
        return existing && isEqual(existing, newItem) ? existing : { ...newItem };
      });

      this.dataSource.data = nextData;
    }

    // this.applyFilter(this.searchTerm$.getValue());
  }

  private buildTableData(menus: IMenuGroup[]): IMenuGroupTableData[] {
    return menus.map(menu => {
      if (menu.translation && Array.isArray(menu.translation) && menu.translation.length) {
        return {...menu, translation: new MatTableDataSource(menu.translation)};
      }

      return menu;
    });
  }

  applyFilter(searchTerm: string): void {
    this.searchTerm$.next(searchTerm);
    this.dataSource.filter = searchTerm;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  toggleRow(element: IMenuGroupTableData): void {
    if (element.translation && (element.translation as MatTableDataSource<IMenu>).data?.length) {
      if (this.expandedElements.includes(element.id)) {
        this.expandedElements = this.expandedElements.filter(id => id !== element.id);
      } else {
        this.expandedElements.push(element.id);
      }
    }
  }

  isExpanded(element: IMenuGroupTableData): boolean {
    return this.expandedElements.includes(element.id);
  }

  openEdit(id: number): void {
    const menuGroup = this.originalMenus.find(menu => menu.id === id);

    if (menuGroup?.ownerType !== 'RESTAURANT') {
      return;
    }

    this.dialog.open(EditMenuComponent, {
      data: {
        restaurantId: this.restaurantId,
        isMainMenu: this.isMainMenu,
        originalId: menuGroup?.id,
        menusGroup: menuGroup
          ? menuGroup.translation?.length > 0
            ? [menuGroup, ...menuGroup.translation]
            : [menuGroup]
          : []
      }
    });
  }

  openCopy(id: number): void {
    const menuGroup = this.originalMenus.find(menu => menu.id === id);

    if (!menuGroup) {
      return;
    }

    this.dialog.open(AddMenuComponent, {
      data: {
        mode: 'copy',
        restaurantId: this.restaurantId,
        happyHours: menuGroup.happyHours,
        source: menuGroup,
      }
    });
  }

  createFilter(data: IMenuGroupTableData, searchTerm: string): boolean {
    const querySanitized = searchTerm.trim().toLowerCase();

    const matchesString = (value?: string | null): boolean =>
        value?.toLowerCase().includes(querySanitized) ?? false;

    const matchesFileName = (file?: MenuFile | null): boolean =>
        file ? matchesString(file.name) : false;

    const matchesTranslation = (): boolean =>
        data.translation instanceof MatTableDataSource &&
        data.translation.data.some((translation) =>
            [translation.name, translation.language, translation.file?.name].some(matchesString) ||
            (querySanitized === 'hidden' && translation.hidden) ||
            (querySanitized === 'no icon' && !translation.icon)
        );

    return (
        matchesString(data.name) ||
        matchesString(data.language) ||
        matchesFileName(data.file) ||
        (querySanitized === 'hidden' && data.hidden) ||
        (querySanitized === 'no icon' && !data.icon) ||
        matchesTranslation()
    );
  }

  onPageChange(event: PageEvent): void {
    sessionStorage.setItem('menu.pageSize', event.pageSize.toString());
  }
}
